import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import { FlexStack } from '~/components/atomic';
import { CodeInput } from '~/components/common';
import { useSignUpStepStore, useUserStore } from '~/store';
import useToast from '~/store/useToast';
import InnerLayout from '../Layout/InnerLayout';
import * as S from './VerifyEmail.styled';

interface VerifyEmailProps {
  onNextStep: () => void;
}

export default function VerifyEmail({ onNextStep }: VerifyEmailProps) {
  const { showToast } = useToast();
  const { email: userEmail, password: userPassword } = useUserStore(
    (state) => ({
      email: state.email,
      password: state.password,
    }),
  );
  const { signUpEmail, signUpPassword } = useSignUpStepStore((state) => ({
    signUpEmail: state.verifyEmail.email,
    signUpPassword: state.createPassword.password,
  }));
  const setSubProgress = useSignUpStepStore((state) => state.setSubProgress);
  const [verificationCode, setVerificationCode] = useState('');
  const [resetCodeInput, setResetCodeInput] = useState(false);

  const email = userEmail || signUpEmail;
  const password = userPassword || signUpPassword;
  const [codeError, setCodeError] = useState('');
  const disabled = verificationCode.length !== 6;

  useEffect(() => {
    setSubProgress(disabled ? 0.5 : 1);
  }, [disabled, setSubProgress]);

  useEffect(() => {
    if (email && password) handleResendCode(email, password);
  }, [email, password]);

  useEffect(() => {
    if (resetCodeInput) {
      setResetCodeInput(false);
    }
  }, [resetCodeInput]);

  const handleNextStep = async () => {
    try {
      const { isSignUpComplete } = await confirmSignUp({
        username: email,
        confirmationCode: verificationCode,
      });

      if (isSignUpComplete) {
        onNextStep();
      }
    } catch (error: any) {
      Sentry.captureException(error);

      if (error.statusCode === 400 || error.name === 'CodeMismatchException') {
        setResetCodeInput(true);
        showToast('error', 'Invalid verification code. Please try again.');
      } else {
        setCodeError('Server error, try again');
        showToast('error', `${error.name}: ${error.message}`);
      }
    }
  };

  const handleResendCode = async (email: string, password: string) => {
    try {
      const handleSignUpInfo = {
        username: email,
        password,
        options: {
          userAttributes: { email },
          autoSignIn: false,
        },
      };
      await resendSignUpCode(handleSignUpInfo);
      showToast('success', 'Successfully resent the code.');
    } catch (error: any) {
      console.error(error);
      showToast('error', `${error.name}: ${error.message}`);
    }
  };

  const buttonProps = {
    title: 'Next',
    disabled,
    onClick: handleNextStep,
  };

  return (
    <InnerLayout buttonProps={buttonProps}>
      <FlexStack spacing={24}>
        <FlexStack spacing={8}>
          <S.EmailNotice>
            <span>A verification code has been sent to</span>
            <strong>{email}</strong>
          </S.EmailNotice>
          <S.VerifyNotice>
            <p>
              Please check your inbox and enter the verification code below to
              verify your email address. The code will expire in 24 hours.
            </p>
          </S.VerifyNotice>
        </FlexStack>

        <S.CodeInputBox>
          <CodeInput
            onFocus={() => setCodeError('')}
            isError={!!codeError}
            setVerificationCode={setVerificationCode}
            resetCode={resetCodeInput}
          />
          <S.ButtonBox>
            <S.ResendButton onClick={() => handleResendCode(email, password)}>
              Resend code
            </S.ResendButton>
          </S.ButtonBox>
        </S.CodeInputBox>
      </FlexStack>
    </InnerLayout>
  );
}
